/**
 * API Client for Path of Reflection Backend
 * 
 * Connects to the existing AWS Lambda backend with:
 * - Firebase ID token authentication
 * - Proper error handling and retries
 * - Type safety with backend contracts
 * - Feature flag support for gradual rollout
 */

import { firebaseAuthService } from '../../../frontend/services/firebaseAuth.service';
import { fakeApi } from './fake';
import type {
  Colony,
  Brain,
  AltMe,
  Thread,
  Message,
  User,
  KnowledgeDoc,
  ChatRequest,
  ChatResponse,
  KnowledgeListResponse,
  KnowledgeAddRequest,
  KnowledgeAddResponse,
  KnowledgeDeleteRequest,
  KnowledgeContextRequest,
  IndexingJob,
  ApiError,
  FeatureFlags,
  BrainPersona
} from '../lib/types';

// Configuration
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://ma3yuktnyh.execute-api.us-east-1.amazonaws.com';
const WS_BASE = import.meta.env.VITE_WS_BASE_URL || 'wss://ma3yuktnyh.execute-api.us-east-1.amazonaws.com';
const USE_FAKE = import.meta.env.VITE_USE_FAKE_BACKEND === '1';

// Feature flags from environment
export const featureFlags: FeatureFlags = {
  backendIntegration: true, // Temporarily force enabled
  websockets: import.meta.env.VITE_ENABLE_WEBSOCKETS === 'true',
  fileUpload: import.meta.env.VITE_ENABLE_FILE_UPLOAD === 'true',
};

// Utility functions
function traceId(): string {
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

async function getIdToken(): Promise<string | null> {
  try {
    return await firebaseAuthService.getIdToken();
  } catch (error) {
    console.warn('Failed to get Firebase ID token:', error);
    return null;
  }
}

// HTTP client with interceptors
class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    retries = 2
  ): Promise<T> {
    const token = await getIdToken();
    const headers: Record<string, string> = {
      ...this.defaultHeaders,
      'X-Trace-Id': traceId(),
      ...((options.headers as Record<string, string>) || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${this.baseUrl}${path}`;
    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      if (import.meta.env.VITE_DEBUG_API === 'true') {
        console.log(`[API] ${config.method || 'GET'} ${url}`, config.body ? JSON.parse(config.body as string) : undefined);
      }

      if (import.meta.env.VITE_DEBUG_API === 'true') {
        console.log('[API] ->', { method: config.method || 'GET', url, hasAuth: !!headers.Authorization, traceId: headers['X-Trace-Id'] });
      }

      const response = await fetch(url, config);
      const text = await response.text();
      
      let data: unknown = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = text;
      }

      if (!response.ok) {
        const error: ApiError = {
          error: typeof data === 'string' ? data : (data as any)?.error || 'Request failed',
          details: (data as any)?.details,
          code: (data as any)?.code,
          traceId: headers['X-Trace-Id'],
        };
        if (import.meta.env.VITE_DEBUG_API === 'true') {
          console.log('[API] <-', { status: response.status, ok: response.ok, traceId: headers['X-Trace-Id'], bodyKeys: typeof data === 'object' ? Object.keys((data as any) || {}) : 'text' });
        }
        throw new ApiClientError(error, response.status);
      }

      if (import.meta.env.VITE_DEBUG_API === 'true') {
        console.log(`[API] Response ${response.status}:`, data);
        console.log('[API] <-', { status: response.status, ok: response.ok, traceId: headers['X-Trace-Id'], bodyKeys: typeof data === 'object' ? Object.keys((data as any) || {}) : 'text' });
      }

      return data as T;
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw error;
      }

      // Retry logic for network errors
      if (retries > 0 && this.isRetryableError(error)) {
        await this.delay(1000 * (3 - retries)); // exponential backoff
        return this.request<T>(path, options, retries - 1);
      }

      throw new ApiClientError({
        error: 'Network error',
        details: error instanceof Error ? error.message : String(error),
      }, 0);
    }
  }

  private isRetryableError(error: unknown): boolean {
    // Retry on network errors, not on 4xx client errors
    return !(error instanceof ApiClientError && error.status >= 400 && error.status < 500);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // HTTP methods
  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.request<T>(`${path}${query}`, { method: 'GET' });
  }

  async post<T>(path: string, body?: Record<string, unknown>): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(path: string, body?: Record<string, unknown>): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(path: string, body?: Record<string, unknown>): Promise<T> {
    return this.request<T>(path, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

// Custom error class
export class ApiClientError extends Error {
  constructor(
    public apiError: ApiError,
    public status: number
  ) {
    super(apiError.error);
    this.name = 'ApiClientError';
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }
}

// Create client instance
const client = new ApiClient(API_BASE);

// API methods organized by domain
export const api = {
  // User/Auth
  async getProfile(): Promise<User> {
    // For now, get user info from Firebase directly
    const token = await getIdToken();
    if (!token) throw new Error('Not authenticated');
    
    // TODO: Implement /me endpoint on backend
    // For now, return basic user info from Firebase
    const user = await firebaseAuthService.getCurrentUser();
    if (!user) throw new Error('No user found');
    
    return {
      uid: user.uid,
      email: user.email || undefined,
      displayName: user.displayName || undefined,
    };
  },

  // Colonies
  async getColonies(): Promise<Colony[]> {
    if (USE_FAKE) {
      const resp = await fakeApi.getColonies();
      return resp.colonies as unknown as Colony[];
    }
    if (!featureFlags.backendIntegration) {
      return [];
    }
    // Backend expects userId query; pull from Firebase
    const user = await firebaseAuthService.getCurrentUser();
    const userId = user?.uid;
    console.log('🔍 DEBUG: getColonies user:', user ? { uid: user.uid, email: user.email } : null);
    const resp = await client.get<{ colonies?: Colony[] }>('/colonies', userId ? { userId } : undefined);
    // Normalize: backend may return { colonies, traceId } or a direct array
    const normalizeColonies = (cols: any[]): Colony[] =>
      (cols || []).map((c: any) => ({
        ...c,
        brains: (c?.brains || []).map((b: any) => ({
          // Ensure UI fields exist even if backend only sends persona fields
          ...b,
          name: (b?.name ?? b?.persona?.name ?? (b?.id === 'alt-me' ? 'Alt-me' : '')) as string,
          color: (b?.color ?? b?.persona?.color ?? undefined) as string | undefined,
          modelPreference: (b?.modelPreference ?? b?.persona?.modelPreference ?? (b?.persona as any)?.modelPref ?? undefined) as string | undefined,
          persona: b?.persona
            ? {
                ...b.persona,
                modelPreference:
                  (b.persona as any).modelPreference ??
                  (b.persona as any).modelPref ??
                  'flash-lite',
              }
            : b?.persona,
        })),
      }));

    if (Array.isArray(resp)) {
      return normalizeColonies(resp as any[]);
    }
    if (resp && Array.isArray((resp as any).colonies)) {
      return normalizeColonies((resp as any).colonies as any[]);
    }
    // Fallback: unexpected shape
    console.warn('[API] getColonies returned unexpected shape:', resp);
    return [];
  },

  async getColony(id: string): Promise<Colony> {
    return client.get<Colony>(`/colonies/${id}`);
  },

  async createColony(colony: Omit<Colony, 'id' | 'createdAt' | 'updatedAt'>): Promise<Colony> {
    // Align with backend bootstrap endpoint
    const user = await firebaseAuthService.getCurrentUser();
    const userId = user?.uid;
    return client.post<Colony>('/api/colony/create', { userId, name: colony.name });
  },

  // Brains
  async getBrains(colonyId: string): Promise<Brain[]> {
    return client.get<Brain[]>(`/colonies/${colonyId}/brains`);
  },

  async createBrain(colonyId: string, brain: {
    userId: string;
    name: string;
    personality?: string;
    tone?: 'professional' | 'casual' | 'friendly';
    humor?: 'none' | 'dry' | 'light' | 'witty';
    modelPreference?: string;
    specialties?: string[];
    color?: string;
  }): Promise<Brain> {
    if (USE_FAKE) {
      const resp = await fakeApi.createBrain(colonyId, brain);
      return {
        id: resp.id,
        colonyId,
        name: resp.name,
        role: 'Specialized Assistant',
        capabilities: [],
        status: 'active',
        personality: brain.personality || '',
        tone: brain.tone,
        humor: brain.humor,
        modelPreference: brain.modelPreference,
        color: brain.color,
        specialties: brain.specialties || [],
        createdAt: new Date().toISOString()
      } as Brain;
    }
    const requestBody = {
      userId: brain.userId,
      colonyId,
      name: brain.name,
      personality: brain.personality || '',
      tone: brain.tone,
      humor: brain.humor,
      modelPreference: brain.modelPreference,
      color: brain.color,
      specialties: brain.specialties || []
    };
    
    console.log('🎭 DEBUG: Creating brain with personality:', requestBody);
    const resp = await client.post<any>('/api/colony/add-brain', requestBody);
    // Backend returns updated colony; extract the newly added brain by name/personality
    if (resp?.colony && Array.isArray(resp.colony.brains)) {
      const match = resp.colony.brains.find((b: any) =>
        (b?.persona?.name || b?.name) === brain.name || (b?.persona?.personality === brain.personality)
      ) || resp.colony.brains[resp.colony.brains.length - 1];
      return {
        id: match?.id || `${brain.name.toLowerCase().replace(/[^a-z0-9]+/g,'-').slice(0,24)}`,
        colonyId,
        name: brain.name,
        role: 'Specialized Assistant',
        capabilities: [],
        status: 'active',
        personality: brain.personality || '',
        tone: brain.tone,
        humor: brain.humor,
        modelPreference: brain.modelPreference,
        color: brain.color,
        specialties: brain.specialties || [],
        createdAt: new Date().toISOString()
      } as Brain;
    }
    // Fallback to simple brain echo
    return {
      id: `${brain.name.toLowerCase().replace(/[^a-z0-9]+/g,'-').slice(0,24)}`,
      colonyId,
      name: brain.name,
      role: 'Specialized Assistant',
      capabilities: [],
      status: 'active',
      personality: brain.personality || '',
      tone: brain.tone,
      humor: brain.humor,
      modelPreference: brain.modelPreference,
      specialties: brain.specialties || [],
      createdAt: new Date().toISOString()
    } as Brain;
  },

  async updateBrain(colonyId: string, brainId: string, persona: Partial<BrainPersona>): Promise<Brain> {
    const user = await firebaseAuthService.getCurrentUser();
    if (!user) throw new Error('User not authenticated');
    
    const requestBody = {
      userId: user.uid,
      persona,
    };
    
    console.log('🎭 DEBUG: Updating brain with persona:', { colonyId, brainId, persona });
    
    try {
      const response = await client.put<any>(`/api/colony/${colonyId}/brain/${brainId}`, requestBody);
      
      // Backend may return updated colony or just the brain
      if (response?.colony && Array.isArray(response.colony.brains)) {
        // Find the updated brain in the colony
        const updatedBrain = response.colony.brains.find((b: any) => b.id === brainId);
        if (updatedBrain) {
          return {
            id: updatedBrain.id,
            colonyId,
            name: updatedBrain.persona?.name || updatedBrain.name || persona.name || '',
            role: 'Specialized Assistant',
            capabilities: [],
            status: 'active',
            personality: updatedBrain.persona?.personality || persona.personality || '',
            tone: updatedBrain.persona?.tone || persona.tone,
            humor: updatedBrain.persona?.humor || persona.humor,
            modelPreference: updatedBrain.persona?.modelPreference || persona.modelPreference,
            color: updatedBrain.persona?.color || persona.color,
            specialties: updatedBrain.persona?.specialties || persona.specialties || [],
            createdAt: updatedBrain.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          } as Brain;
        }
      }
      
      // Fallback: construct brain from response or persona
      return {
        id: brainId,
        colonyId,
        name: persona.name || '',
        role: 'Specialized Assistant',
        capabilities: [],
        status: 'active',
        personality: persona.personality || '',
        tone: persona.tone,
        humor: persona.humor,
        modelPreference: persona.modelPreference,
        color: persona.color,
        specialties: persona.specialties || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as Brain;
      
    } catch (error) {
      console.error('Failed to update brain:', error);
      throw error; // Re-throw the ApiClientError from the client
    }
  },

  async deleteBrain(colonyId: string, brainId: string): Promise<void> {
    if (USE_FAKE) {
      // No-op for fake backend; assume success
      return Promise.resolve();
    }
    const user = await firebaseAuthService.getCurrentUser();
    if (!user) throw new Error('Not authenticated');
    return client.delete<void>(`/api/colony/${colonyId}/brain/${brainId}`, { userId: user.uid });
  },

  // Alt-me (personal orchestrator)
  async getAltMeThreads(colonyId: string): Promise<Thread[]> {
    return client.get<Thread[]>(`/colonies/${colonyId}/altme/threads`);
  },

  async createAltMeThread(colonyId: string, title?: string): Promise<Thread> {
    return client.post<Thread>(`/colonies/${colonyId}/altme/threads`, { title });
  },

  async getMessages(threadId: string, cursor?: string): Promise<{ messages: Message[]; nextCursor?: string }> {
    const params = cursor ? { cursor } : undefined;
    return client.get(`/threads/${threadId}/messages`, params);
  },

  async sendMessage(threadId: string, content: string): Promise<Message> {
    return client.post<Message>(`/threads/${threadId}/messages`, { content });
  },

  // Chat (existing endpoint)
  async sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
    // Route all chat through the unified interact endpoint
    const user = await firebaseAuthService.getCurrentUser();
    const userId = user?.uid || request.userId;
    if (!userId) {
      throw new Error('Not authenticated');
    }
    if (!request.colonyId) {
      throw new Error('colonyId is required');
    }
    const body = {
      userId,
      mode: 'chat' as const,
      brainId: 'alt-me',
      message: request.message ?? request.messages?.[request.messages.length - 1]?.content ?? '',
    };
    return client.post<ChatResponse>(`/api/colony/${request.colonyId}/interact`, body);
  },

  // Knowledge (existing endpoints)
  async listKnowledge(colonyId: string): Promise<KnowledgeListResponse> {
    return client.get<KnowledgeListResponse>('/api/knowledge/list', { colonyId });
  },

  async addKnowledge(request: KnowledgeAddRequest): Promise<KnowledgeAddResponse> {
    return client.post<KnowledgeAddResponse>('/api/knowledge/add', request as unknown as Record<string, unknown>);
  },

  async deleteKnowledge(request: KnowledgeDeleteRequest): Promise<void> {
    return client.delete<void>('/api/knowledge/delete', request as unknown as Record<string, unknown>);
  },

  async getKnowledgeContext(request: KnowledgeContextRequest): Promise<unknown> {
    return client.post('/api/knowledge/context', request as unknown as Record<string, unknown>);
  },

  // Jobs (for indexing status)
  async getJob(jobId: string): Promise<IndexingJob> {
    return client.get<IndexingJob>(`/jobs/${jobId}`);
  },

  // Feedback (existing endpoint)
  async sendFeedback(feedback: Record<string, unknown>): Promise<void> {
    return client.post<void>('/feedback', feedback);
  },

  // Traces (existing endpoint)
  async getTraces(): Promise<unknown[]> {
    return client.get<unknown[]>('/traces');
  },

  // Generic HTTP methods for direct API access
  get: <T = unknown>(path: string, params: Record<string, string>) => client.get<T>(path, params),
  post: <T = unknown>(path: string, data: Record<string, unknown>) => client.post<T>(path, data),
  put: <T = unknown>(path: string, data: Record<string, unknown>) => client.put<T>(path, data),
  delete: <T = unknown>(path: string, data: Record<string, unknown>) => client.delete<T>(path, data),
};

// WebSocket connection for real-time updates
export class ApiWebSocket {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  async connect(threadId: string): Promise<WebSocket> {
    if (!featureFlags.websockets) {
      throw new Error('WebSockets not enabled');
    }

    const token = await getIdToken();
    if (!token) {
      throw new Error('Authentication required for WebSocket connection');
    }

    const wsUrl = `${WS_BASE}/ws/threads/${threadId}?token=${encodeURIComponent(token)}`;
    
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        resolve(this.ws!);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        reject(error);
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.handleReconnect(threadId);
      };
    });
  }

  private async handleReconnect(threadId: string) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      
      console.log(`Attempting to reconnect WebSocket in ${delay}ms (attempt ${this.reconnectAttempts})`);
      
      setTimeout(() => {
        this.connect(threadId);
      }, delay);
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(data: Record<string, unknown>) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }
}

export default api;
